import React from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Badge } from 'primereact/badge';
import { Divider } from 'primereact/divider';

const ConfiguredAlertDetailModal = ({ visible, onHide, alertConfig }) => {
  if (!alertConfig) return null;

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSensorTypeLabel = (sensorType) => {
    const types = {
      'temperature': 'Température',
      'humidity': 'Humidité',
      'door': 'Porte',
      'battery': 'Batterie',
      'rssi': 'Signal'
    };
    return types[sensorType] || sensorType;
  };

  const getStatusSeverity = (isActive) => {
    return isActive ? 'success' : 'danger';
  };

  const getStatusLabel = (isActive) => {
    return isActive ? 'Active' : 'Inactive';
  };

  const getTypeLabel = (type) => {
    if (!type) return 'Non défini';
    const types = {
      'alert': 'Alerte',
      'warning': 'Avertissement',
      'info': 'Information',
      'critical': 'Critique',
      'error': 'Erreur'
    };
    return types[type.toLowerCase()] || type;
  };

  const footer = (
    <div className="flex justify-content-end">
      <Button 
        label="Fermer" 
        icon="pi pi-times" 
        onClick={onHide}
        className="p-button-outlined"
      />
    </div>
  );

  return (
    <Dialog
      header="Détails de l'Alerte Préconfigurée"
      visible={visible}
      onHide={onHide}
      footer={footer}
      style={{ width: '700px', marginTop: '5vh' }}
      modal
      className="p-fluid"
      position="center"
    >
      <div className="flex flex-column gap-4">
        {/* En-tête avec code et statut */}
        <div className="flex justify-content-between align-items-center p-3 border-round surface-100">
          <div>
            <h3 className="m-0 text-primary">{alertConfig.label || alertConfig.code || alertConfig.Code}</h3>
            <p className="m-0 text-600 text-sm mt-1">
              {alertConfig.code && alertConfig.label && (
                <>Code: {alertConfig.code} | </>
              )}
              ID: {alertConfig.uid || alertConfig.ID}
            </p>
          </div>
          <Badge 
            value={getStatusLabel(alertConfig.isActive || alertConfig.IsActive)}
            severity={getStatusSeverity(alertConfig.isActive || alertConfig.IsActive)}
            className="text-sm"
          />
        </div>

        {/* Informations principales */}
        <div className="grid">
          <div className="col-12 md:col-6">
            <div className="field">
              <label className="font-semibold text-900">Type d'Alerte</label>
              <div className="flex align-items-center gap-2 mt-1">
                <i className={`pi ${(alertConfig.type || alertConfig.Type) === 'critical' ? 'pi-exclamation-triangle' : 
                  (alertConfig.type || alertConfig.Type) === 'warning' ? 'pi-exclamation-circle' : 
                  (alertConfig.type || alertConfig.Type) === 'info' ? 'pi-info-circle' : 'pi-bell'}`}></i>
                <span>{getTypeLabel(alertConfig.type || alertConfig.Type)}</span>
              </div>
            </div>
          </div>
          
          <div className="col-12 md:col-6">
            <div className="field">
              <label className="font-semibold text-900">Type de Capteur</label>
              <div className="flex align-items-center gap-2 mt-1">
                <i className={`pi ${alertConfig.sensorType === 'temperature' ? 'pi-sun' : 
                  alertConfig.sensorType === 'humidity' ? 'pi-cloud' : 
                  alertConfig.sensorType === 'door' ? 'pi-home' : 'pi-circle'}`}></i>
                <span>{getSensorTypeLabel(alertConfig.sensorType)}</span>
              </div>
            </div>
          </div>

          <div className="col-12 md:col-6">
            <div className="field">
              <label className="font-semibold text-900">Condition de Déclenchement</label>
              <div className="mt-1">
                <Badge value={alertConfig.condition} severity="warning" className="text-sm" />
              </div>
            </div>
          </div>

          <div className="col-12 md:col-6">
            <div className="field">
              <label className="font-semibold text-900">Capteur</label>
              <div className="mt-1">
                {alertConfig.enginLabel && (
                  <div className="flex align-items-center gap-2 mb-1">
                    <i className="pi pi-tag text-blue-600"></i>
                    <span className="font-semibold">{alertConfig.enginLabel}</span>
                  </div>
                )}
                {alertConfig.macAddr && (
                  <div className="flex align-items-center gap-1">
                    <i className="pi pi-wifi text-blue-400 text-xs"></i>
                    <code className="text-xs text-600">{alertConfig.macAddr}</code>
                  </div>
                )}
                {!alertConfig.enginLabel && !alertConfig.macAddr && 'Non spécifié'}
              </div>
            </div>
          </div>
        </div>

        <Divider />

        {/* Message et description */}
        <div className="grid">
          <div className="col-12">
            <div className="field">
              <label className="font-semibold text-900">Message d'Alerte</label>
              <div className="mt-2 p-3 border-round surface-50 text-sm">
                {alertConfig.message || alertConfig.Message || 'Aucun message défini'}
              </div>
            </div>
          </div>

          {alertConfig.description && (
            <div className="col-12">
              <div className="field">
                <label className="font-semibold text-900">Description</label>
                <div className="mt-2 p-3 border-round surface-50 text-sm">
                  {alertConfig.description}
                </div>
              </div>
            </div>
          )}
        </div>

        <Divider />

        {/* Informations de configuration */}
        <div className="grid">
          <div className="col-12 md:col-6">
            <div className="field">
              <label className="font-semibold text-900">Date de Création</label>
              <div className="mt-1 text-sm">
                <i className="pi pi-calendar mr-2 text-500"></i>
                {formatDate(alertConfig.createdAt || alertConfig.CreatedAt)}
              </div>
            </div>
          </div>

          <div className="col-12 md:col-6">
            <div className="field">
              <label className="font-semibold text-900">Dernière Modification</label>
              <div className="mt-1 text-sm">
                <i className="pi pi-clock mr-2 text-500"></i>
                {formatDate(alertConfig.updatedAt || alertConfig.UpdatedAt) || 'Non modifiée'}
              </div>
            </div>
          </div>

          {alertConfig.users && (
            <div className="col-12 md:col-6">
              <div className="field">
                <label className="font-semibold text-900">Utilisateurs Notifiés</label>
                <div className="mt-1 text-sm">
                  <i className="pi pi-users mr-2 text-500"></i>
                  {alertConfig.users}
                </div>
              </div>
            </div>
          )}
          
          {alertConfig.emails && (
            <div className="col-12 md:col-6">
              <div className="field">
                <label className="font-semibold text-900">Emails de notification</label>
                <div className="mt-1 text-sm">
                  <i className="pi pi-envelope mr-2 text-500"></i>
                  {alertConfig.emails}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Informations techniques */}
        {(alertConfig.srcObject || alertConfig.srcID || alertConfig.srcId) && (
          <>
            <Divider />
            <div className="grid">
              {alertConfig.srcObject && (
                <div className="col-12 md:col-6">
                  <div className="field">
                    <label className="font-semibold text-900">Objet Source</label>
                    <div className="mt-1 text-sm font-mono">
                      {alertConfig.srcObject}
                    </div>
                  </div>
                </div>
              )}

              {(alertConfig.srcID || alertConfig.srcId) && (
                <div className="col-12 md:col-6">
                  <div className="field">
                    <label className="font-semibold text-900">ID Source</label>
                    <div className="mt-1 text-sm font-mono">
                      {alertConfig.srcID || alertConfig.srcId}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
};

export default ConfiguredAlertDetailModal;
